import { useState } from 'react';
import moment from 'moment';
import { toast } from 'react-hot-toast';
import { reportsApi } from 'src/api/reportsApi';
import { toPascalCase, parseExternalEfforts, getExternalEffortsForCompany, updateExternalEffortInReport } from 'src/utils/reportUtils';

export const useExternalEfforts = () => {
  const [externalEffortModalOpen, setExternalEffortModalOpen] = useState(false);
  const [selectedCompanyForEffort, setSelectedCompanyForEffort] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingEffortId, setEditingEffortId] = useState(null);
  const [externalEffortForm, setExternalEffortForm] = useState({
    outlet: '',
    title: '',
    date: moment().format('YYYY-MM-DD'),
    status: 'submitted to journalist',
    link: '',
    outletLink: '',
  });
  const [isSubmittingEffort, setIsSubmittingEffort] = useState(false);

  const resetForm = () => {
    setExternalEffortForm({
      outlet: '',
      title: '',
      date: moment().format('YYYY-MM-DD'),
      status: 'submitted to journalist',
      link: '',
      outletLink: '',
    });
    setIsEditMode(false);
    setEditingEffortId(null);
  };

  const handleOpenExternalEffortModal = (company) => {
    setSelectedCompanyForEffort(company);
    resetForm();
    setExternalEffortModalOpen(true);
  };

  const handleOpenEditExternalEffortModal = (company, effort) => {
    setSelectedCompanyForEffort(company);
    setIsEditMode(true);
    setEditingEffortId(effort.id);
    setExternalEffortForm({
      outlet: effort.outlet || effort.mediaOutlet || '',
      title: effort.title || '',
      date: effort.date || moment().format('YYYY-MM-DD'),
      status: effort.status || 'submitted to journalist',
      link: effort.link || '',
      outletLink: effort.outletLink || '',
    });
    setExternalEffortModalOpen(true);
  };

  const handleSubmitExternalEffort = async (formData, allReports, allReportsMutate, dateRanges, dateFilter) => {
    // Validate required fields
    if (!formData.outlet.trim() || !formData.title.trim() || !formData.date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmittingEffort(true);

    try {
      const selectedRange = dateRanges[dateFilter];
      const startDate = selectedRange.start.format('YYYY-MM-DD');
      const endDate = selectedRange.end.format('YYYY-MM-DD');
      const pascalCaseCompany = toPascalCase(selectedCompanyForEffort);

      // Find existing report for this company and date range
      // Prefer the report that already has external efforts (handles duplicate report entries)
      const matchingReports = allReports?.filter(
        (report) =>
          report.company === pascalCaseCompany &&
          moment(report.startDate).format('YYYY-MM-DD') === startDate &&
          moment(report.endDate).format('YYYY-MM-DD') === endDate
      ) || [];
      const existingReport = matchingReports.find((r) => r.externalEfforts) || matchingReports[0];

      let externalEffortsArray = [];

      // Parse existing external efforts if any
      if (existingReport?.externalEfforts) {
        externalEffortsArray = parseExternalEfforts(existingReport.externalEfforts);
      }

      if (isEditMode && editingEffortId) {
        // Update existing external effort using utility
        await updateExternalEffortInReport(reportsApi, {
          company: selectedCompanyForEffort,
          dateRanges,
          dateFilter,
          allReports,
          effortId: editingEffortId,
          updatedFields: {
            id: editingEffortId,
            outlet: formData.outlet.trim(),
            title: formData.title.trim(),
            date: formData.date,
            status: formData.status,
            link: formData.link.trim(),
            outletLink: formData.outletLink.trim(),
            mediaOutlet: formData.outlet.trim(),
          },
        });
      } else {
        // Create new external effort
        const newExternalEffort = {
          id: Date.now(), // Temporary ID for frontend use
          outlet: formData.outlet.trim(),
          title: formData.title.trim(),
          date: formData.date,
          status: formData.status,
          link: formData.link.trim(),
          outletLink: formData.outletLink.trim(),
          mediaOutlet: formData.outlet.trim(),
          updateTime: new Date().toISOString(),
        };

        // Add new external effort
        externalEffortsArray.push(newExternalEffort);

        const externalEffortsJson = JSON.stringify(externalEffortsArray);

        if (existingReport) {
          // Update existing report
          await reportsApi.update(existingReport.id, pascalCaseCompany, startDate, endDate, externalEffortsJson);
        } else {
          // Create new report
          await reportsApi.create(pascalCaseCompany, startDate, endDate, externalEffortsJson);
        }
      }

      // Refresh reports data
      allReportsMutate();

      const successMessage = isEditMode ? 'Additional effort updated successfully' : 'Additional effort added successfully';
      toast.success(successMessage);
      setExternalEffortModalOpen(false);
    } catch (error) {
      // console.error('Error saving additional effort:', error);
      const errorMessage = isEditMode ? 'Failed to update additional effort' : 'Failed to add additional effort';
      toast.error(errorMessage);
    } finally {
      setIsSubmittingEffort(false);
    }
  };

  const handleRemoveExternalEffort = async (company, effortId, allReports, allReportsMutate, dateRanges, dateFilter) => {
    try {
      const selectedRange = dateRanges[dateFilter];
      const startDate = selectedRange.start.format('YYYY-MM-DD');
      const endDate = selectedRange.end.format('YYYY-MM-DD');
      const pascalCaseCompany = toPascalCase(company);

      // Find existing report — prefer the one with external efforts (handles duplicates)
      const matchingReports = allReports?.filter(
        (report) =>
          report.company === pascalCaseCompany &&
          moment(report.startDate).format('YYYY-MM-DD') === startDate &&
          moment(report.endDate).format('YYYY-MM-DD') === endDate
      ) || [];
      const existingReport = matchingReports.find((r) => r.externalEfforts) || matchingReports[0];

      if (!existingReport?.externalEfforts) {
        toast.error('No additional efforts found to remove');
        return;
      }

      const externalEffortsArray = parseExternalEfforts(existingReport.externalEfforts);

      // Remove the effort with the specified ID
      const filteredEfforts = externalEffortsArray.filter((effort) => effort.id !== effortId);
      const externalEffortsJson = filteredEfforts.length > 0 ? JSON.stringify(filteredEfforts) : '';

      // Update the report
      await reportsApi.update(existingReport.id, pascalCaseCompany, startDate, endDate, externalEffortsJson);

      // Refresh reports data
      allReportsMutate();

      toast.success('Additional effort removed successfully');
    } catch (error) {
      // console.error('Error removing additional effort:', error);
      toast.error('Failed to remove additional effort');
    }
  };

  return {
    // State
    externalEffortModalOpen,
    selectedCompanyForEffort,
    externalEffortForm,
    isSubmittingEffort,
    isEditMode,
    editingEffortId,

    // Actions
    setExternalEffortModalOpen,
    setSelectedCompanyForEffort,
    handleOpenExternalEffortModal,
    handleOpenEditExternalEffortModal,
    handleSubmitExternalEffort,
    handleRemoveExternalEffort,
    resetForm,

    // Utilities
    getExternalEffortsForCompany,
  };
};
